import React, { useContext, useState, memo } from 'react'
import PropTypes from 'prop-types'

import { Backdrop } from '../index'
import { LanguageContext } from '../../../context/LanguageContext'
import { useToggle } from '../../CustomHooks/useCustomHooks'
import { util } from '../../../util'

const Menu = ({
    renderProps,
    className,
    children,
    listWidth = 'lg',
    placement = 'middle',
    tipPlacement = 'middle',
    hover = false,
    ...rest
}) => {
    const { margin } = { ...rest }
    const [showMenuList, toggleMenuList] = useToggle(false)
    const [ShowComponent, setShowComponent] = useState(null)

    const _showMenuListComponent = component => {
        toggleMenuList()
        setShowComponent(component() || null)
    }

    const unMountComponentHandler = () => setShowComponent(null)

    const menuListClass = util.joinClasses(
        'menu__list tip bottom',
        listWidth,
        placement,
        `tip-${tipPlacement}`
    )

    /* renderProps will render JSX (HorizontalListIcon or MenuIcon etc), finally we are wrapping a div element to the 
    rendered JSX and assigning the event handlers to change the state of the showMenuList using React Clone Element */
    const renderMenuView = () =>
        React.cloneElement(
            <span className={util.joinClasses('menu__content', hover && 'hover')} {...rest}>
                {renderProps()}
            </span>,
            {
                onClick: toggleMenuList,
            }
        )

    /* on Clicking the Menu View item which was rendered from the above function (renderMenuView), menu list state (showMenuList) will 
    toggle and menu list component will appear according to its position and placement specified in the props */
    const renderMenuListItems = () =>
        showMenuList ? (
            <div className={util.joinClasses('menu__wrapper', margin)}>
                {/* The following code is to render all children wrapped in between the menu component
                 * and setShowComponent callback will execute on clicking the menu item */}
                <div className={menuListClass}>
                    {React.Children.toArray(children).map(child =>
                        React.cloneElement(child, {
                            setShowComponent: _showMenuListComponent,
                        })
                    )}
                </div>
            </div>
        ) : null

    return (
        <div
            data-testid={'menu'}
            className={util.joinClasses('menu', className, showMenuList ? 'open' : 'close')}
        >
            {renderMenuView()}
            {renderMenuListItems()}
            {showMenuList && <Backdrop closeBackdrop={toggleMenuList} />}

            {/* ShowComponent is JSX object, using React.cloneElement we can render it */}
            {/* The following statement is to mount the component when the user clicks on the menu list item, and
                we are sending a close callback to un-mount the mounted component */}
            {ShowComponent && React.cloneElement(ShowComponent, { close: unMountComponentHandler })}
        </div>
    )
}

const MenuItem = ({ children, onClick, show = true, setShowComponent, ...rest }) => {
    const { rtl } = useContext(LanguageContext)

    if (!show) {
        return null
    }
    const itemClickHandler = () => (onClick ? setShowComponent(onClick) : undefined)
    return (
        <div className={`menu__list--item ${rtl && 'rtl'}`} onClick={itemClickHandler} {...rest}>
            {children}
        </div>
    )
}

Menu.MenuItem = memo(MenuItem)

Menu.propTypes = {
    /** @param renderProps {callback} is a callback function which invokes and renders menu icon or text etc. */
    renderProps: PropTypes.func,

    /** @param className {String} class name that will over ride the menu class */
    className: PropTypes.string,

    /** @param children {JSX} renders the JSX which was wrapped in between Menu Component */
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),

    /** @param listWidth {String} -> ( 'sm' | 'md' | 'lg' | 'xl' ) width of the menu list */
    listWidth: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),

    /** @param placement {String} -> ('left' | 'middle' | 'right') determines the position of the menu list */
    placement: PropTypes.oneOf(['left', 'middle', 'right']),

    /** @param tipPlacement {String} -> ('left' | 'middle' | 'right') determines the position of the tip */
    tipPlacement: PropTypes.oneOf(['left', 'middle', 'right']),

    /** @param hover {Boolean} hover State */
    hover: PropTypes.bool,

    /** @param rest {props} remaining props */
    rest: PropTypes.object,
}

MenuItem.propTypes = {
    children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    onClick: PropTypes.func,
    show: PropTypes.bool,
    setShowComponent: PropTypes.func,
    rest: PropTypes.object,
}

export default Menu
